import type { Distribution, GpuSample, JobStat, ModelStats, RunnerInfo, SpeedUnit, StatsRange } from "@studio/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/AppShell";
import { MeterBar, Sparkline } from "@/components/Sparkline";
import { Segmented, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { useLive } from "@/lib/events";
import { cn, modelLabel } from "@/lib/format";

const SPEED_LABEL: Record<SpeedUnit, string> = {
  sec_per_image: "s per picture",
  sec_per_output_sec: "s per second of video",
  realtime_factor: "× faster than real time",
  tokens_per_sec: "words per second",
  sec_per_megapixel: "s per megapixel",
};

function secs(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value < 1) return `${value.toFixed(2)} s`;
  if (value < 90) return `${Math.round(value)} s`;
  const m = Math.floor(value / 60);
  if (m < 60) return `${m} min ${String(Math.round(value % 60)).padStart(2, "0")} s`;
  return `${(value / 3600).toFixed(1)} h`;
}

function num(value: number | null | undefined, digits = 1): string {
  return value === null || value === undefined ? "—" : value.toFixed(digits);
}

function avg(dist: Distribution | null | undefined): string {
  return secs(dist?.avg ?? null);
}

/** What the GPUs are doing now, how fast things have been, and what everyone is waiting for. */
export function ActivityPage() {
  const [range, setRange] = useState<StatsRange>("24h");
  const live = useQuery({ queryKey: ["stats", "live"], queryFn: api.statsLive, refetchInterval: 15_000 });
  const summary = useQuery({ queryKey: ["stats", "summary", range], queryFn: () => api.statsSummary(range), refetchInterval: 60_000 });
  const jobs = useQuery({ queryKey: ["stats", "jobs"], queryFn: () => api.statsJobs(30), refetchInterval: 60_000 });
  const telemetry = useQuery({ queryKey: ["stats", "telemetry"], queryFn: () => api.telemetry(60), refetchInterval: 60_000 });
  const pushed = useLive((s) => s.gpus);
  const runners = useLive((s) => s.runners);

  const gpus: GpuSample[] = pushed.length ? pushed : (live.data?.gpus ?? []);
  const fleet: RunnerInfo[] = runners.length ? runners : (live.data?.runners ?? []);
  const queue = live.data?.queue ?? [];

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Activity" />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-8 px-4 pb-10 pt-4 md:px-6">
          <section className="flex flex-col gap-3">
            <h2 className="text-[17px] font-semibold">Graphics cards right now</h2>
            {gpus.length === 0 ? (
              <p className="text-[14px] text-mute">No GPU server is reporting. Check the servers below.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {gpus.map((gpu) => {
                  const series = telemetry.data?.series.find((s) => s.runnerId === gpu.runnerId && s.index === gpu.index);
                  const memFraction = gpu.memoryUsedMb && gpu.memoryTotalMb ? gpu.memoryUsedMb / gpu.memoryTotalMb : 0;
                  return (
                    <article key={`${gpu.runnerId}-${gpu.index}`} className="rounded-[var(--radius-control)] border border-line bg-panel p-3">
                      <p className="truncate text-[13px] text-mute">
                        {gpu.runnerName} · GPU {gpu.index}
                      </p>
                      <p className="truncate text-[14px]">{gpu.name}</p>
                      <div className="mt-2 flex items-end justify-between gap-3">
                        <p className="tabular text-[30px] font-semibold leading-none">
                          {gpu.utilization === null ? "—" : `${Math.round(gpu.utilization)}%`}
                        </p>
                        <div className="min-w-0 flex-1">
                          <Sparkline
                            points={(series?.points ?? []).map((p) => p.utilization)}
                            max={100}
                            label={`Load of GPU ${gpu.index} on ${gpu.runnerName} over the last hour`}
                          />
                        </div>
                      </div>
                      <p className="tabular mt-2 text-[13px] text-mute">
                        {gpu.memoryUsedMb !== null && gpu.memoryTotalMb !== null
                          ? `${(gpu.memoryUsedMb / 1024).toFixed(1)} of ${(gpu.memoryTotalMb / 1024).toFixed(0)} GB`
                          : "memory unknown"}
                      </p>
                      <MeterBar fraction={memFraction} className="mt-1" />
                      <p className="tabular mt-2 text-[13px] text-mute">
                        {gpu.temperatureC !== null ? `${Math.round(gpu.temperatureC)} °C` : "—"}
                        {gpu.powerW !== null ? ` · ${Math.round(gpu.powerW)} W` : ""}
                        {gpu.powerLimitW !== null ? ` of ${Math.round(gpu.powerLimitW)} W` : ""}
                        {gpu.fanPercent !== null ? ` · fan ${Math.round(gpu.fanPercent)}%` : ""}
                      </p>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[17px] font-semibold">GPU servers</h2>
            <div className="flex flex-col gap-2">
              {fleet.map((runner) => (
                <article key={runner.id} className="rounded-[var(--radius-control)] border border-line bg-panel p-3">
                  <header className="flex flex-wrap items-center gap-2">
                    <span className={cn("size-2 rounded-full", runner.online ? "bg-ok" : "bg-mute")} aria-hidden />
                    <p className="text-[15px] font-medium">{runner.name}</p>
                    {runner.reservedFor?.length ? (
                      <span className="rounded-full bg-raised px-2 py-0.5 text-[12px] text-mute">
                        only {runner.reservedFor.map((r) => r.displayName).join(", ")}
                      </span>
                    ) : null}
                    {runner.disabled ? <span className="rounded-full bg-danger/15 px-2 py-0.5 text-[12px] text-danger">off</span> : null}
                  </header>
                  {runner.downloads && runner.downloads.state !== "done" ? (
                    <div className="mt-2">
                      <p className={cn("tabular text-[13px]", runner.downloads.state === "failed" ? "text-danger" : "text-mute")}>
                        {runner.downloads.state === "failed"
                          ? `Downloading the models failed: ${runner.downloads.error ?? "unknown reason"}`
                          : runner.downloads.state === "verifying"
                            ? "Checking the downloaded models…"
                            : `Downloading models: ${(runner.downloads.doneBytes / 1e9).toFixed(1)} of ${(runner.downloads.totalBytes / 1e9).toFixed(1)} GB`}
                        {runner.downloads.currentFile ? ` · ${runner.downloads.currentFile}` : ""}
                      </p>
                      <MeterBar fraction={runner.downloads.totalBytes ? runner.downloads.doneBytes / runner.downloads.totalBytes : 0} className="mt-1" />
                    </div>
                  ) : null}
                  <ul className="mt-2 flex flex-col gap-1">
                    {runner.slots.map((slot) => (
                      <li key={slot.id} className="flex flex-wrap items-center gap-x-2 text-[13px]">
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[12px] font-medium",
                            slot.state === "busy" && "bg-film/20 text-signal",
                            slot.state === "loading" && "bg-raised text-mute",
                            slot.state === "idle" && "bg-ok/15 text-ok",
                            slot.state === "offline" && "bg-raised text-mute",
                          )}
                        >
                          {slot.state}
                        </span>
                        <span className="text-mute">
                          {slot.loadedModel ? `${modelLabel(slot.loadedModel)} loaded` : "nothing loaded"}
                          {slot.job ? ` · ${slot.job.mine ? "your" : `${slot.job.ownerName}'s`} ${slot.job.type}` : ""}
                          {slot.backendOk ? "" : " · backend not answering"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
              {fleet.length === 0 ? <p className="text-[14px] text-mute">No GPU servers are connected.</p> : null}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[17px] font-semibold">In line now</h2>
            {queue.length === 0 ? (
              <p className="text-[14px] text-mute">Nothing is waiting.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line/60 rounded-[var(--radius-control)] border border-line">
                {queue.map((job) => (
                  <li key={job.ref} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px]">
                        {job.mine ? "Your" : `${job.ownerName}'s`} {job.type}
                        {job.resolution ? ` · ${job.resolution}` : ""}
                        {job.durationSec ? ` · ${Math.round(job.durationSec)} s` : ""}
                        {job.count && job.count > 1 ? ` · ${job.count} pictures` : ""}
                      </p>
                      <p className="tabular text-[13px] text-mute">
                        {job.status === "running" ? "making it now" : job.queuePosition ? `in line, ${job.queuePosition - 1} ahead` : job.status}
                        {job.runnerName ? ` · ${job.runnerName}` : ""}
                        {job.etaSec ? ` · about ${secs(job.etaSec)} left` : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-[17px] font-semibold">How fast it has been</h2>
              <Segmented
                value={range}
                onChange={(value) => setRange(value)}
                options={[
                  { value: "24h" as StatsRange, label: "24 hours" },
                  { value: "7d" as StatsRange, label: "7 days" },
                ]}
              />
            </div>
            {summary.isLoading ? (
              <Spinner />
            ) : summary.data ? (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { label: "Made", value: String(summary.data.totals.completed) },
                    { label: "Did not work", value: String(summary.data.totals.failed) },
                    { label: "Chat answers", value: String(summary.data.totals.chats) },
                    { label: "GPU time", value: secs(summary.data.totals.gpuSec) },
                  ].map((tile) => (
                    <div key={tile.label} className="rounded-[var(--radius-control)] border border-line bg-panel px-3 py-2.5">
                      <p className="tabular text-[22px] font-semibold leading-tight">{tile.value}</p>
                      <p className="text-[13px] text-mute">{tile.label}</p>
                    </div>
                  ))}
                </div>

                <div className="scrollbar-thin overflow-x-auto rounded-[var(--radius-control)] border border-line">
                  <table className="w-full min-w-[640px] border-collapse text-[14px]">
                    <thead>
                      <tr className="border-b border-line bg-raised text-left text-[12px] uppercase tracking-wide text-mute">
                        <th className="px-3 py-2 font-semibold">Model</th>
                        <th className="px-3 py-2 font-semibold">Made</th>
                        <th className="px-3 py-2 font-semibold">Waited</th>
                        <th className="px-3 py-2 font-semibold">Loading</th>
                        <th className="px-3 py-2 font-semibold">Making</th>
                        <th className="px-3 py-2 font-semibold">Speed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.data.models.map((model: ModelStats) => (
                        <ModelRows key={model.model} model={model} />
                      ))}
                      {summary.data.models.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-3 text-mute">
                            Nothing was made in this period.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                {summary.data.users ? (
                  <div className="scrollbar-thin overflow-x-auto rounded-[var(--radius-control)] border border-line">
                    <table className="w-full min-w-[520px] border-collapse text-[14px]">
                      <thead>
                        <tr className="border-b border-line bg-raised text-left text-[12px] uppercase tracking-wide text-mute">
                          <th className="px-3 py-2 font-semibold">Person</th>
                          <th className="px-3 py-2 font-semibold">Made</th>
                          <th className="px-3 py-2 font-semibold">Did not work</th>
                          <th className="px-3 py-2 font-semibold">Chat answers</th>
                          <th className="px-3 py-2 font-semibold">GPU time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.data.users.map((user) => (
                          <tr key={user.userId} className="border-b border-line/60 last:border-b-0">
                            <td className="px-3 py-2">{user.displayName}</td>
                            <td className="tabular px-3 py-2">{user.completed}</td>
                            <td className="tabular px-3 py-2">{user.failed}</td>
                            <td className="tabular px-3 py-2">{user.chats}</td>
                            <td className="tabular px-3 py-2">{secs(user.gpuSec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </>
            ) : null}
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[17px] font-semibold">Last finished</h2>
            <div className="scrollbar-thin overflow-x-auto rounded-[var(--radius-control)] border border-line">
              <table className="w-full min-w-[640px] border-collapse text-[14px]">
                <thead>
                  <tr className="border-b border-line bg-raised text-left text-[12px] uppercase tracking-wide text-mute">
                    <th className="px-3 py-2 font-semibold">What</th>
                    <th className="px-3 py-2 font-semibold">Whose</th>
                    <th className="px-3 py-2 font-semibold">Server</th>
                    <th className="px-3 py-2 font-semibold">Waited</th>
                    <th className="px-3 py-2 font-semibold">Making</th>
                    <th className="px-3 py-2 font-semibold">Speed</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.data?.map((job: JobStat) => (
                    <tr key={job.ref} className="border-b border-line/60 last:border-b-0">
                      <td className="px-3 py-2">
                        {job.kind}
                        {job.variant ? ` · ${job.variant}` : ""}
                        {job.status !== "completed" ? <span className="ml-2 text-danger">{job.status}</span> : null}
                      </td>
                      <td className="px-3 py-2">{job.mine ? "You" : job.ownerName}</td>
                      <td className="px-3 py-2 text-mute">{job.runnerName ?? "—"}</td>
                      <td className="tabular px-3 py-2">{secs(job.waitSec)}</td>
                      <td className="tabular px-3 py-2">{secs(job.generateSec)}</td>
                      <td className="tabular px-3 py-2">
                        {job.speed === null ? "—" : `${num(job.speed, job.speedUnit === "tokens_per_sec" ? 0 : 1)} ${SPEED_LABEL[job.speedUnit]}`}
                      </td>
                    </tr>
                  ))}
                  {jobs.data?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-3 text-mute">
                        Nothing has finished yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/** A model, plus one row per video resolution when it has them. */
function ModelRows({ model }: { model: ModelStats }) {
  return (
    <>
      <tr className="border-b border-line/60">
        <td className="px-3 py-2">
          {modelLabel(model.model)}
          {model.failed ? <span className="ml-2 text-[13px] text-danger">{model.failed} failed</span> : null}
        </td>
        <td className="tabular px-3 py-2">{model.completed}</td>
        <td className="tabular px-3 py-2">{avg(model.waitSec)}</td>
        <td className="tabular px-3 py-2">{model.loads ? avg(model.loadSec) : "—"}</td>
        <td className="tabular px-3 py-2">{avg(model.generateSec)}</td>
        <td className="tabular px-3 py-2">
          {model.speed.avg === null ? "—" : `${num(model.speed.avg, model.speedUnit === "tokens_per_sec" ? 0 : 1)} ${SPEED_LABEL[model.speedUnit]}`}
        </td>
      </tr>
      {model.variants.map((variant) => (
        <tr key={variant.key} className="border-b border-line/60 text-[13px] text-mute">
          <td className="py-1.5 pl-8 pr-3">{variant.key}</td>
          <td className="tabular px-3 py-1.5">{variant.completed}</td>
          <td className="px-3 py-1.5">—</td>
          <td className="px-3 py-1.5">—</td>
          <td className="tabular px-3 py-1.5">{avg(variant.generateSec)}</td>
          <td className="tabular px-3 py-1.5">
            {variant.speed.avg === null ? "—" : `${num(variant.speed.avg, 1)} ${SPEED_LABEL[model.speedUnit]}`}
          </td>
        </tr>
      ))}
    </>
  );
}
